import { useMemo, useState } from "react";
import client from "../api/client";

export default function PayrollPage() {
  const currentYear = new Date().getFullYear();
  const [form, setForm] = useState({
    employeeId: "",
    payMonth: new Date().getMonth() + 1,
    payYear: currentYear,
    workingDays: 30,
    presentDays: 30,
    deductions: 0,
  });
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const processPayroll = async () => {
    try {
      if (!form.employeeId) return alert("Employee Id is required");
      if (!form.payMonth) return alert("Pay Month is required");
      if (!form.payYear) return alert("Pay Year is required");
      setBusy(true);
      await client.post("/hr/payroll/process", {
        companyId: Number(localStorage.getItem("companyId") || 0),
        employeeId: Number(form.employeeId),
        payMonth: Number(form.payMonth),
        payYear: Number(form.payYear),
        workingDays: Number(form.workingDays),
        presentDays: Number(form.presentDays),
        deductions: Number(form.deductions),
      });
      alert("Payroll processed successfully");
      await load(false);
    } catch (error) {
      console.error("Error processing payroll:", error);
      alert("Failed to process payroll");
    } finally {
      setBusy(false);
    }
  };

  const load = async (validate = true) => {
    try {
      if (validate && (!form.payMonth || !form.payYear)) return alert("Please enter month and year");
      setBusy(true);
      const res = await client.get(`/hr/payroll?month=${form.payMonth}&year=${form.payYear}`);
      setRows(res.data || []);
    } catch (error) {
      console.error("Error loading payroll:", error);
      alert("Failed to load payroll");
    } finally {
      setBusy(false);
    }
  };

  const totals = useMemo(() => rows.reduce((a, r) => ({
    gross: a.gross + Number(r.GrossSalary || r.grossSalary || 0),
    deductions: a.deductions + Number(r.Deductions || r.deductions || 0),
    net: a.net + Number(r.NetSalary || r.netSalary || 0),
  }), { gross: 0, deductions: 0, net: 0 }), [rows]);

  return (
    <div className="container-fluid py-2 app-small">
      <div className="hr-hero card border-0 shadow-sm mb-3">
        <div className="card-body d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div>
            <div className="small text-uppercase fw-bold opacity-75">Human Resources</div>
            <h4 className="mb-1 fw-bold">Payroll Processing</h4>
            <div className="opacity-75">Process salary, deductions and net pay month-wise.</div>
          </div>
          <span className="badge bg-light text-dark rounded-pill px-3 py-2">{form.payMonth}/{form.payYear}</span>
        </div>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-md-4"><div className="hr-stat-card p-3"><div className="text-muted small">Gross Salary</div><div className="h4 mb-0 fw-bold">₹{totals.gross.toFixed(2)}</div></div></div>
        <div className="col-md-4"><div className="hr-stat-card p-3"><div className="text-muted small">Deductions</div><div className="h4 mb-0 fw-bold">₹{totals.deductions.toFixed(2)}</div></div></div>
        <div className="col-md-4"><div className="hr-stat-card p-3"><div className="text-muted small">Net Pay</div><div className="h4 mb-0 fw-bold">₹{totals.net.toFixed(2)}</div></div></div>
      </div>

      <div className="card border-0 shadow-sm mb-3 hr-form-card">
        <div className="card-header bg-white border-0 py-3">
          <div className="hr-section-title">Payroll Input</div>
          <div className="small text-muted">Enter employee ID and monthly attendance details.</div>
        </div>
        <div className="card-body row g-3 pt-0">
          <div className="col-xl-2 col-md-4"><label className="form-label">Employee Id</label><input className="form-control form-control-sm" type="number" value={form.employeeId} onChange={(e) => setForm({ ...form, employeeId: e.target.value })}/></div>
          <div className="col-xl-2 col-md-4"><label className="form-label">Month</label><select className="form-select form-select-sm" value={form.payMonth} onChange={(e) => setForm({ ...form, payMonth: e.target.value })}>{Array.from({length:12},(_,i)=><option key={i+1} value={i+1}>{i+1}</option>)}</select></div>
          <div className="col-xl-2 col-md-4"><label className="form-label">Year</label><input className="form-control form-control-sm" type="number" value={form.payYear} onChange={(e) => setForm({ ...form, payYear: e.target.value })}/></div>
          <div className="col-xl-2 col-md-4"><label className="form-label">Working Days</label><input className="form-control form-control-sm text-end" type="number" value={form.workingDays} onChange={(e) => setForm({ ...form, workingDays: e.target.value })}/></div>
          <div className="col-xl-2 col-md-4"><label className="form-label">Present Days</label><input className="form-control form-control-sm text-end" type="number" value={form.presentDays} onChange={(e) => setForm({ ...form, presentDays: e.target.value })}/></div>
          <div className="col-xl-2 col-md-4"><label className="form-label">Deductions</label><input className="form-control form-control-sm text-end" type="number" value={form.deductions} onChange={(e) => setForm({ ...form, deductions: e.target.value })}/></div>
          <div className="col-12 d-flex gap-2">
            <button className="btn btn-primary btn-sm px-4" onClick={processPayroll} disabled={busy}><i className="bi bi-play-circle me-1"></i>{busy ? "Working..." : "Process Payroll"}</button>
            <button className="btn btn-outline-secondary btn-sm px-4" onClick={() => load()} disabled={busy}><i className="bi bi-search me-1"></i>Load Payroll</button>
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm hr-table-card">
        <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
          <div><div className="hr-section-title">Payroll Register</div><div className="small text-muted">Month-wise processed salary records.</div></div>
          <span className="badge rounded-pill text-bg-primary">{rows.length} records</span>
        </div>
        <div className="table-responsive compact-table-wrap">
          <table className="table table-sm table-hover align-middle mb-0 compact-table">
            <thead><tr><th>EmpCode</th><th>Name</th><th className="text-end">Gross</th><th className="text-end">Deductions</th><th className="text-end">Net</th></tr></thead>
            <tbody>{rows.length ? rows.map((r, i) => <tr key={r.PayrollId || i}><td className="fw-semibold">{r.EmpCode || r.empCode}</td><td>{r.FirstName || r.firstName} {r.LastName || r.lastName}</td><td className="text-end">{Number(r.GrossSalary || r.grossSalary || 0).toFixed(2)}</td><td className="text-end">{Number(r.Deductions || r.deductions || 0).toFixed(2)}</td><td className="text-end fw-bold">{Number(r.NetSalary || r.netSalary || 0).toFixed(2)}</td></tr>) : <tr><td colSpan="5" className="text-center text-muted py-4">No payroll records found</td></tr>}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
